import { Grid, IconButton } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import Eye from 'components/common/icons/Eye'
import TitleWithText from 'components/Patients/PatientGuidelines/PatientBrief/Categories/TitleWithText'
import React, { useCallback, useMemo } from 'react'
import { sortTeeth } from 'utils/giUtils'
import NightTimeOnly from '../Actions/NightTimeOnly'

const useStyles = makeStyles(() => ({
  openSetupButton: {
    padding: 2,
    marginLeft: 6,
    verticalAlign: 'middle'
  }
}))

/**
 * Presentation-only renderer for the elastics category. Shared by the patient-brief
 * viewer and the Copilot tooltip.
 *
 * To expose an inline "open setup" action (eye icon next to each elastic), the owning
 * surface must pass `onOpenSetup`. The callback receives the eye-button element so the
 * caller can anchor a floating popup to it. Without `onOpenSetup`, Elastics stays
 * read-only — no eye icons render.
 *
 * @param {Object} props
 * @param {{ elastics?: Array, nightTimeOnly?: boolean }} props.items
 * @param {(anchorEl: HTMLElement) => void} [props.onOpenSetup]
 */
const Elastics = ({ items, onOpenSetup }) => {
  const classes = useStyles()
  const { elastics = [], nightTimeOnly } = items

  const elasticItems = useMemo(
    () =>
      elastics.map(elastic => ({
        title: elastic.name,
        content: sortTeeth(Object.keys(elastic.selections))
          .map(tooth => `${tooth} (${elastic.selections[tooth]})`)
          .join(', ')
      })),
    [elastics]
  )

  const handleEyeClick = useCallback(
    e => {
      // Prevent the click from bubbling into the GuidelineCategory header (which would
      // toggle the section's collapsed state) or any other clickable ancestor.
      e.stopPropagation()
      onOpenSetup?.(e.currentTarget)
    },
    [onOpenSetup]
  )

  return (
    <>
      <Grid container direction="column" spacing={2}>
        {elasticItems.map(item => (
          <Grid item key={item.title}>
            <TitleWithText
              title={item.title}
              text={
                <>
                  {item.content}
                  {onOpenSetup && (
                    <IconButton size="small" className={classes.openSetupButton} onClick={handleEyeClick}>
                      <Eye />
                    </IconButton>
                  )}
                </>
              }
            />
          </Grid>
        ))}
      </Grid>
      <NightTimeOnly checked={nightTimeOnly} />
    </>
  )
}

export default Elastics
